/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

// @mui material components
import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Pagination from '@mui/material/Pagination';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import TableHeader from 'components/Table/TableHeader';
import MDDateRange from 'components/MDDateRange';

// Material Dashboard 2 PRO React examples
import Navbar from 'examples/Navbars/NavBar';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';

const initFind = {
  search: '',
  order_by: 'tb.id',
  order_sort: 'desc',
  page: 1,
  perPage: 10,
  pageTotal: 0,
  pharmacy: 'ALL',
  fg: 'ALL',
  rm: 'ALL',
  medicine: 'ALL',
  drug_type_9: 'ALL',
  drug_type_10: 'ALL',
  drug_type_11: 'ALL',
  date_start: Func.dateTimeFirstMonth(),
  date_stop: Func.dateTimeEndMonth(),
};

function RpProductBarcode() {
  const mod = 'rp_product_barcode';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [record, setRecord] = useState([]);
  const [show, setShow] = useState(null);
  const [find, setFind] = useState(initFind);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(initFind.order_sort);
  const [orderBy, setOrderBy] = useState(initFind.order_by);
  const [urlExcel, setUrlExcel] = useState('');

  const openShow = (e) => setShow(e.currentTarget);
  const closeShow = () => setShow(null);

  const initHeader = [
    { id: 'tb.id', label: t(`attr.image`), align: 'center', width: 40 },
    { id: 'tb.code', label: 'สินค้า', align: 'left' },
    { id: 'pd.brand_id', label: 'ยี่ห้อ', align: 'left' },
    { id: 'tb.best_id', label: 'Ranking', align: 'left' },
    { id: 'tb.unit_id', label: 'หน่วยนับ', align: 'left' },
    { id: 'pd.fg', label: 'FG', align: 'center' },
    { id: 'pd.rm', label: 'RM', align: 'center' },
    { id: 'pd.pharmacy', label: 'ยา', align: 'center' },
    { id: 'pd.medicine', label: 'ยาสามัญ', align: 'center' },
    { id: 'pd.drug_type_9', label: 'ข.ย.9', align: 'center' },
    { id: 'pd.drug_type_10', label: 'ข.ย.10', align: 'center' },
    { id: 'pd.drug_type_11', label: 'ข.ย.11', align: 'center' },
    { id: 'pd.enable', label: 'เปิดใช้', align: 'center' },
  ];

  const view = (data) => {
    setFind(data);
    setLoading(true);
    setRecord([]);
    closeShow();

    FetchData('POST', `/${mod}`, app.token, {
      lang: app.lang,
      search: data.search,
      page: data.page,
      per_page: data.perPage,
      orderby: data.order_by,
      ordersort: data.order_sort,
      pharmacy: data.pharmacy,
      fg: data.fg,
      rm: data.rm,
      medicine: data.medicine,
      drug_type_9: data.drug_type_9,
      drug_type_10: data.drug_type_10,
      drug_type_11: data.drug_type_11,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);

        setPageTotal(result.total_pages);
        setPageNo(result.page);
      }
      setLoading(false);
    });
  };

  const excel = () => {
    const params = new URLSearchParams();
    params.append('search', find.search);
    params.append('pharmacy', find.pharmacy);
    params.append('fg', find.fg);
    params.append('rm', find.rm);
    params.append('medicine', find.medicine);
    params.append('drug_type_9', find.drug_type_9);
    params.append('drug_type_10', find.drug_type_10);
    params.append('drug_type_11', find.drug_type_11);

    const url = `${urlExcel}/download?${params.toString()}`;
    window.open(url, '_self');
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const renderShow = (
    <Menu
      anchorEl={show}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      open={Boolean(show)}
      onClose={closeShow}
      keepMounted
    >
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 10 })}>
        {t(`element.show_record`, { num: 10 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 20 })}>
        {t(`element.show_record`, { num: 20 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 100 })}>
        {t(`element.show_record`, { num: 100 })}
      </MenuItem>
    </Menu>
  );

  const handleSort = (e, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);

    view({ ...find, order_by: property, order_sort: isAsc ? 'desc' : 'asc' });
  };

  const init = () => {
    FetchData('GET', `/${mod}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        view(find);
        setUrlExcel(result.excel);
      } else {
        Func.exitApp();
      }
    });
  };

  const renderSearch = () => {
    return (
      <Card sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDBox
              noValidate
              component='form'
              role='form'
              onSubmit={(e) => {
                e.preventDefault();
                view({ ...find, period: 'ALL', page: 1 });
              }}
            >
              <MDInput
                name='searchform'
                fullWidth
                value={find.search}
                type='search'
                onChange={(e) => setFind({ ...find, search: e.target.value, page: 1 })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>{t('element.search')} :</InputAdornment>
                  ),
                  autoComplete: 'off',
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDInput
              fullWidth
              value={find.pharmacy}
              defaultValue='ALL'
              select
              onChange={(e) => view({ ...find, pharmacy: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <span>ยา :</span>
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value='ALL'>ทั้งหมด</MenuItem>
              <MenuItem value='Y'>ใช่</MenuItem>
              <MenuItem value='N'>ไม่ใช่</MenuItem>
            </MDInput>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDInput
              fullWidth
              value={find.fg}
              defaultValue='ALL'
              select
              onChange={(e) => view({ ...find, fg: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <span>สินค้าสำเร็จรูป :</span>
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value='ALL'>ทั้งหมด</MenuItem>
              <MenuItem value='Y'>ใช่</MenuItem>
              <MenuItem value='N'>ไม่ใช่</MenuItem>
            </MDInput>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDInput
              fullWidth
              value={find.rm}
              defaultValue='ALL'
              select
              onChange={(e) => view({ ...find, rm: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <span>วัตถุดิบ :</span>
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value='ALL'>ทั้งหมด</MenuItem>
              <MenuItem value='Y'>ใช่</MenuItem>
              <MenuItem value='N'>ไม่ใช่</MenuItem>
            </MDInput>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDInput
              fullWidth
              value={find.medicine}
              defaultValue='ALL'
              select
              onChange={(e) => view({ ...find, medicine: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <span>ยาสามัญประจำบ้าน :</span>
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value='ALL'>ทั้งหมด</MenuItem>
              <MenuItem value='Y'>ใช่</MenuItem>
              <MenuItem value='N'>ไม่ใช่</MenuItem>
            </MDInput>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDInput
              fullWidth
              value={find.drug_type_9}
              defaultValue='ALL'
              select
              onChange={(e) => view({ ...find, drug_type_9: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <span>ข.ย. 9 :</span>
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value='ALL'>ทั้งหมด</MenuItem>
              <MenuItem value='Y'>ใช่</MenuItem>
              <MenuItem value='N'>ไม่ใช่</MenuItem>
            </MDInput>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDInput
              fullWidth
              value={find.drug_type_10}
              defaultValue='ALL'
              select
              onChange={(e) => view({ ...find, drug_type_10: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <span>ข.ย. 10 :</span>
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value='ALL'>ทั้งหมด</MenuItem>
              <MenuItem value='Y'>ใช่</MenuItem>
              <MenuItem value='N'>ไม่ใช่</MenuItem>
            </MDInput>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={2}>
            <MDInput
              fullWidth
              value={find.drug_type_11}
              defaultValue='ALL'
              select
              onChange={(e) => view({ ...find, drug_type_11: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <span>ข.ย. 11 :</span>
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value='ALL'>ทั้งหมด</MenuItem>
              <MenuItem value='Y'>ใช่</MenuItem>
              <MenuItem value='N'>ไม่ใช่</MenuItem>
            </MDInput>
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={1}>
            <MDBox display='flex' justifyContent='end' gap={1}>
              <MDButton variant='gradient' color='success' onClick={excel}>
                <Icon>table_view</Icon>&nbsp;Excel
              </MDButton>
            </MDBox>
          </Grid>
        </Grid>
      </Card>
    );
  };

  const renderList = () => {
    return (
      <MDBox mb={7}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3} md={2} lg={1}>
            <MDButton
              variant='contained'
              color='dark'
              size='large'
              fullWidth
              sx={{ pt: 1.6, pb: 1.6 }}
              href='/report_product'
            >
              <Icon>content_paste_search</Icon>
            </MDButton>
          </Grid>
          <Grid item xs={12} sm={9} md={10} lg={11}>
            {renderSearch()}
          </Grid>
          <Grid item xs={12}>
            <TableContainer component={Paper} sx={{ minHeight: 400 }}>
              <Table aria-label='datatable table'>
                <TableHeader
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleSort}
                  data={initHeader}
                />
                <TableBody>
                  {record.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{Func.imageDisplay(row)}</TableCell>
                      <TableCell align='left'>
                        <MDBox>{row.code}</MDBox>
                        <MDBox className='cut' width={200}>
                          {row.name}
                        </MDBox>
                      </TableCell>
                      <TableCell>{row.brand_name}</TableCell>
                      <TableCell>{row.best_name}</TableCell>
                      <TableCell>{row.unit_name}</TableCell>
                      <TableCell align='center'>{Func.enableDisplay(row.fg)}</TableCell>
                      <TableCell align='center'>{Func.enableDisplay(row.rm)}</TableCell>
                      <TableCell align='center'>{Func.enableDisplay(row.pharmacy)}</TableCell>
                      <TableCell align='center'>{Func.enableDisplay(row.medicine)}</TableCell>
                      <TableCell align='center'>{Func.enableDisplay(row.drug_type_9)}</TableCell>
                      <TableCell align='center'>{Func.enableDisplay(row.drug_type_10)}</TableCell>
                      <TableCell align='center'>{Func.enableDisplay(row.drug_type_11)}</TableCell>
                      <TableCell align='center'>{Func.enableDisplay(row.enable)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12}>
            <Card sx={{ p: 2 }}>
              <MDBox display='flex' justifyContent='space-between' alignItems='flex-start'>
                <MDButton variant={show ? 'contained' : 'outlined'} color='dark' onClick={openShow}>
                  {t(`element.show_record`, { num: find.perPage })}&nbsp;
                  <Icon>keyboard_arrow_down</Icon>
                </MDButton>
                {renderShow}
                <Pagination
                  count={pageTotal}
                  variant='outlined'
                  color='secondary'
                  showFirstButton
                  showLastButton
                  page={pageNo}
                  onChange={(e, p) => view({ ...find, page: p })}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    init();
  }, [id]);

  return (
    <MDBox>
      <Navbar route={[t(`menu.report_product`), t(`menu.rp_product_barcode`)]} />
      {renderList()}
      {renderLoading()}
    </MDBox>
  );
}

export default RpProductBarcode;
